import { Badge, Box, Flex, Spinner, Switch, Text } from '@chakra-ui/react'
import { ChangeEvent, MouseEvent, useMemo, useState } from 'react'

import { FormSelect } from '../../../components/Select'
import { Tooltip } from '../../../components/Tooltip'
import { useAppToast } from '../../../helpers'
import ContestsService from '../../../services/Contests/ContestsService'
import {
  IArtworkShowcaseState,
  IContestArtworkForEvaluation,
  IShowcaseTakenPosition
} from '../../../services/Contests/interfaces'

interface IArtworkShowcaseControlProps {
  artwork: IContestArtworkForEvaluation
  /** Slots já tomados no concurso inteiro, para não oferecer um que é de outra obra. */
  takenPositions: IShowcaseTakenPosition[]
  /** Quantos slots a vitrine oferece, como a API informa na listagem. */
  maxPosition: number
  /** view_contest_votes: o estado da vitrine é informação, não controle. */
  readOnly?: boolean
  /**
   * Chamado só depois de a API confirmar — o pai decide se repinta a lista.
   *
   * `wasSelected` viaja junto porque quem sabe o estado anterior desta obra é
   * este card, que acabou de receber a prop; a página derivaria o mesmo dado de
   * uma lista que pode estar uma gravação atrás.
   */
  onChanged: (artId: number, state: IArtworkShowcaseState, wasSelected: boolean) => void
}

/**
 * Curadoria da vitrine do site numa obra (ABRA-314).
 *
 * A regra automática — avaliada por todos e média acima de 4,5 — vive no
 * backend, e é ela que chega em `showcase_selected`. O que este controle faz é
 * traduzir um clique em qual **override** gravar: voltar ao `null` quando o novo
 * estado coincide com o que a regra já diria, e gravar a exceção quando não
 * coincide. Sem isso, marcar e desmarcar uma obra deixaria um `true` cravado
 * onde antes havia "siga a regra", e ela nunca mais sairia da vitrine sozinha.
 */
export default function ArtworkShowcaseControl({
  artwork,
  takenPositions,
  maxPosition,
  readOnly = false,
  onChanged
}: IArtworkShowcaseControlProps) {
  const service = useMemo(() => new ContestsService(), [])
  const toast = useAppToast()
  const [isSaving, setIsSaving] = useState(false)
  // Valor exibido enquanto a gravação está em voo. Zerado no `finally`: no
  // sucesso o pai já repintou a obra e a prop assume; no erro, a prop antiga
  // volta sozinha, então não há rollback para escrever à mão.
  const [optimistic, setOptimistic] = useState<{ selected: boolean; position: number | null } | null>(null)

  const isSelected = optimistic ? optimistic.selected : artwork.showcase_selected
  const position = optimistic ? optimistic.position : artwork.showcase_position

  // Um slot só pode ser de uma obra: o que já é de outra entra na lista
  // desabilitado e com o nome de quem o tem, porque "2ª posição indisponível"
  // sem dizer de quem só faz o curador procurar às cegas pelo concurso.
  const positionOptions = useMemo(() => {
    const holders = new Map(
      takenPositions
        .filter((taken) => taken.artwork_id !== artwork.id)
        .map((taken) => [taken.position, taken.artwork_name])
    )

    return Array.from({ length: maxPosition }, (_, index) => {
      const slot = index + 1
      const holder = holders.get(slot)

      return {
        value: slot,
        label: holder ? `${slot}ª posição · ${holder}` : `${slot}ª posição`,
        isDisabled: holder !== undefined
      }
    })
  }, [takenPositions, artwork.id, maxPosition])

  if (readOnly) {
    if (!isSelected) return null

    return (
      <Badge colorScheme="pink" mb="4">
        Na vitrine{position !== null ? ` · ${position}ª` : ''}
      </Badge>
    )
  }

  const save = async (next: { selected: boolean; position: number | null }, override: boolean | null) => {
    if (isSaving) return

    // Lido antes da gravação: depois dela a prop já pode ter sido repintada.
    const wasSelected = artwork.showcase_selected

    setOptimistic(next)
    setIsSaving(true)
    try {
      const state = await service.updateArtShowcase(artwork.id, {
        showcase_override: override,
        showcase_position: next.position
      })
      onChanged(artwork.id, state, wasSelected)
    } catch (error) {
      toast.error('Erro ao atualizar a vitrine do site', error)
    } finally {
      setOptimistic(null)
      setIsSaving(false)
    }
  }

  const handleToggle = (event: ChangeEvent<HTMLInputElement>) => {
    const selected = event.target.checked
    // Se o novo estado é o que a regra já diz, o override some e a obra volta a
    // seguir a nota; senão ele grava a exceção.
    const override = selected === artwork.showcase_auto ? null : selected
    // Desligar leva a posição junto: um slot órfão continuaria ocupado para as
    // outras obras do concurso sem nada na tela para explicar por quê.
    save({ selected, position: selected ? position : null }, override)
  }

  const handlePositionChange = (value: string) => {
    save({ selected: isSelected, position: value === '' ? null : Number(value) }, artwork.showcase_override)
  }

  return (
    // O card inteiro abre a avaliação, então nenhum clique daqui pode subir.
    <Box mb="4" onClick={(event: MouseEvent) => event.stopPropagation()}>
      <Flex align="center" gap="2" mb="2">
        <Tooltip
          label={
            artwork.showcase_auto
              ? 'Escolhida pela nota. Desmarque para tirá-la da vitrine.'
              : 'Fora da regra da nota. Marque para incluí-la mesmo assim.'
          }
          // Sem o teto a dica fica larga e rasa, e no card da ponta esquerda o
          // Popper a empurra para dentro da tela, tirando-a de cima do switch.
          maxW="210px"
          textAlign="center"
          // O `ref` do Switch vai para o input escondido, não para o trilho, e a
          // dica ficaria ancorada num elemento de 1px fora do lugar. O span que
          // o `shouldWrapChildren` cria abraça o controle visível.
          shouldWrapChildren
        >
          <Switch
            isChecked={isSelected}
            isDisabled={isSaving}
            onChange={handleToggle}
            aria-label="Exibir esta obra na vitrine do site"
            display="flex"
          />
        </Tooltip>

        <Text fontSize="12px" color={isSelected ? 'gray.700' : 'gray.500'} fontWeight="600" noOfLines={1}>
          Vitrine do site
        </Text>

        {isSaving && <Spinner size="xs" color="blue.500" />}
      </Flex>

      {/*
        O select fica sempre à vista e apenas trancado enquanto a obra está fora
        da vitrine: aparecendo só depois do clique, ele empurrava o resto do card
        para baixo e escondia que a ordem existe.
      */}
      <FormSelect
        // `sm` porque o card é uma peça repetida da grade e este é o único
        // controle dentro dele — não há vizinho `md` de que destoar.
        size="sm"
        value={position ?? ''}
        onChange={handlePositionChange}
        options={positionOptions}
        placeholder="Sem posição fixa"
        isDisabled={!isSelected || isSaving}
        aria-label="Posição da obra na vitrine do site"
        title="Posição fixa na vitrine do site. Sem posição, a obra entra na ordem da nota."
      />
    </Box>
  )
}
